'use client';

import { useState } from 'react';
import Link from 'next/link';
import Script from 'next/script';
import Breadcrumbs from '@/components/ui/breadcrumbs';

const pvc440Prices = [
  { height: '0.5m', prices: ['£10', '£15', '£15', '£20', '£30', '£40', '£50'] },
  { height: '0.75m', prices: ['£15', '£20', '£20', '£30', '£45', '£55', '£65'] },
  { height: '1m', prices: ['£15', '£20', '£25', '£45', '£65', '£85', '£105'] },
];
const pvc440Widths = ['0.5m', '0.75m', '1m', '2m', '3m', '4m', '5m'];

const pvc510Prices = [
  { height: '0.5m', prices: ['£20', '£20', '£32', '£45', '£55', '£70', '£85'] },
  { height: '1m', prices: ['£20', '£32', '£60', '£85', '£125', '£155', '£180'] },
  { height: '1.30m', prices: ['£20', '£35', '£70', '£100', '£130', '£160', '£190'] },
];
const pvc510Widths = ['0.5m', '1m', '2m', '3m', '4m', '5m', '6m'];

const meshPrices = [
  { height: '0.5m', prices: ['£20', '£25', '£35', '£50', '£60', '£75', '£90'] },
  { height: '1m', prices: ['£25', '£35', '£65', '£90', '£130', '£160', '£185'] },
  { height: '1.30m', prices: ['£30', '£40', '£75', '£110', '£140', '£170', '£200'] },
];
const meshWidths = ['0.5m', '1m', '2m', '3m', '4m', '5m', '6m'];

const event650Widths = ['0.5m', '1m', '2m', '3m', '4m', '5m', '6m'];
const event650Prices = [
  { height: '0.5m', prices: ['£25', '£30', '£35', '£50', '£60', '£75', '£90'] },
  { height: '1m', prices: ['£30', '£30', '£65', '£90', '£130', '£160', '£190'] },
  { height: '1.3m', prices: ['£30', '£35', '£70', '£100', '£140', '£170', '£200'] },
];

const magneticPrices = [
  { height: '0.30m', prices: ['£30', '£40', '£50', '£70', '£100', '£160', '£200'] },
  { height: '0.40m', prices: ['£40', '£50', '£60', '£80', '£120', '£170', '£230'] },
  { height: '0.50m', prices: ['£50', '£60', '£70', '£90', '£150', '£200', '£260'] },
  { height: '0.60m', prices: ['£60', '£70', '£80', '£100', '£180', '£230', '£290'] },
];
const magneticWidths = ['0.30m', '0.40m', '0.50m', '1m', '2m', '3m', '4m'];

const stickerSizes = ['40mm Round', '60mm Round', '80mm Round', '100mm Round', 'A6 Size', 'A5 Size'];
const stickerPrices = [
  { qty: '50',  prices: ['£20', '£25', '£30', '£40', '£60', '£80'],  perUnit: ['0.40/pcs', '0.50/pcs', '0.60/pcs', '0.80/pcs', '1.20/pcs', '1.60/pcs'] },
  { qty: '100', prices: ['£30', '£40', '£50', '£60', '£100', '£140'], perUnit: ['0.30/pcs', '0.40/pcs', '0.50/pcs', '0.60/pcs', '1.00/pcs', '1.40/pcs'] },
  { qty: '250', prices: ['£60', '£80', '£100', '£110', '£170', '£250'], perUnit: ['0.24/pcs', '0.32/pcs', '0.40/pcs', '0.44/pcs', '0.68/pcs', '1.00/pcs'] },
  { qty: '500', prices: ['£90', '£100', '£150', '£180', '£250', '£350'], perUnit: ['0.18/pcs', '0.20/pcs', '0.30/pcs', '0.36/pcs', '0.50/pcs', '0.70/pcs'] },
];

const faqs = [
  {
    question: 'How long does banner printing take?',
    answer: 'Most banner orders are ready within 1–3 working days.',
  },
  {
    question: 'Do banners come with eyelets?',
    answer: 'Yes, all PVC banners include eyelets as standard.',
  },
  {
    question: 'Can you design my banner?',
    answer: 'Yes, we offer artwork setup and design support if required.',
  },
  {
    question: 'Do you offer same-day banner printing?',
    answer: 'Depending on size and workload, same-day service may be available.',
  },
  {
    question: 'Can I collect locally?',
    answer: 'Yes, collection is available from Great Barr, Birmingham (B42 2HQ).',
  },
];

function PricingTable({
  widths,
  rows,
}: {
  widths: string[];
  rows: { height: string; prices: string[] }[];
}) {
  return (
    <div className="overflow-x-auto -mx-4 px-4">
      <table className="w-full text-sm border-collapse min-w-[500px]">
        <thead>
          <tr>
            <th className="text-left p-3 bg-black text-white font-semibold">Height / Width</th>
            {widths.map((w) => (
              <th key={w} className="p-3 bg-black text-white font-semibold text-center">
                {w}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.height} className={i % 2 === 0 ? 'bg-white' : 'bg-[var(--color-off-white)]'}>
              <td className="p-3 font-semibold border-b border-[var(--color-border)]">{row.height}</td>
              {row.prices.map((price, j) => (
                <td key={j} className="p-3 text-center border-b border-[var(--color-border)]">
                  {price}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[var(--color-border)]">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left"
      >
        <span className="font-semibold pr-4">{question}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          className={`w-5 h-5 flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
      </button>
      {open && (
        <p className="pb-5 text-sm text-[var(--color-text-secondary)] leading-relaxed">
          {answer}
        </p>
      )}
    </div>
  );
}

const videoSchemaData = {
  '@context': 'https://schema.org',
  '@type': 'VideoObject',
  name: 'Banner Printing Process at OK T-Shirt Birmingham',
  description:
    'Watch our in-house banner printing process using professional large-format equipment at our Great Barr, Birmingham print shop.',
  thumbnailUrl: 'https://img.youtube.com/vi/uuUPacT8BsE/maxresdefault.jpg',
  uploadDate: '2026-01-15',
  contentUrl: 'https://youtube.com/shorts/uuUPacT8BsE',
  embedUrl: 'https://www.youtube.com/embed/uuUPacT8BsE',
  publisher: {
    '@type': 'Organization',
    name: 'OK T-Shirt',
    url: 'https://oktshirt.co.uk',
  },
};

export default function BannerPrintingPage() {
  return (
    <div>
      <Breadcrumbs items={[{ label: 'Banner Printing' }]} />
      {/* VideoObject Schema */}
      <Script
        id="video-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(videoSchemaData) }}
      />

      {/* Hero Section */}
      <section className="py-16 lg:py-24 bg-black">
        <div className="container">
          <div className="max-w-[48rem] mx-auto text-center">
            <h1 className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-6">
              Banner Printing Birmingham
            </h1>
            <p className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 tracking-tight leading-tight text-white">
              Professional PVC &amp; Mesh Banner Printing in Birmingham
            </p>
            <p className="text-lg text-gray-300 mb-8 leading-relaxed">
              Durable outdoor banners printed in-house with fast turnaround from our Great Barr print shop.
            </p>
            <ul className="flex flex-wrap justify-center gap-x-6 gap-y-3 text-sm text-gray-300 mb-10">
              <li className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-green-400" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                440gsm &amp; 510gsm PVC
              </li>
              <li className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-green-400" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                Mesh banners for construction sites
              </li>
              <li className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-green-400" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                Eyelets included
              </li>
              <li className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-green-400" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                No minimum order
              </li>
              <li className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-green-400" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                Fast local turnaround
              </li>
            </ul>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                href="/contact"
                className="inline-flex items-center justify-center px-8 py-3 text-sm font-semibold bg-white text-black hover:bg-gray-100 transition-colors uppercase tracking-wider"
              >
                Get a Quote
              </Link>
              <a
                href="tel:02045208521"
                className="inline-flex items-center justify-center px-8 py-3 text-sm font-semibold border border-white text-white hover:bg-white hover:text-black transition-colors uppercase tracking-wider"
              >
                Call Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-16 lg:py-24">
        <div className="container">
          <div className="max-w-[48rem] mx-auto">
            <p className="text-[var(--color-text-secondary)] leading-relaxed mb-4">
              We provide high-quality banner printing in Birmingham for businesses, construction companies, retail stores and events. All banners are printed in-house using professional large-format equipment to ensure accurate colour, sharp detail and long-lasting durability.
            </p>
            <p className="text-[var(--color-text-secondary)] leading-relaxed">
              Collection available from Great Barr (B42 2HQ). We also serve Sutton Coldfield, Erdington, Northfield and Birmingham city centre.
            </p>
          </div>
        </div>
      </section>

      {/* 440gsm PVC Pricing */}
      <section id="440gsm-pvc-banner" className="scroll-mt-36 py-16 lg:py-24 bg-[var(--color-off-white)]">
        <div className="container">
          <div className="max-w-[56rem] mx-auto">
            <h2 className="text-2xl lg:text-3xl font-bold mb-3">440gsm Standard PVC Banner</h2>
            <p className="text-[var(--color-text-secondary)] mb-8 leading-relaxed">
              Our most popular option. Ideal for shop-desk fronts, receptions, events and general indoor use.
            </p>
            <PricingTable widths={pvc440Widths} rows={pvc440Prices} />
            <p className="text-sm text-[var(--color-text-muted)] mt-4 mb-6">
              Custom sizes available — priced per m².
            </p>
            <p className="text-sm font-semibold mb-3">Features:</p>
            <ul className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-[var(--color-text-secondary)]">
              <li className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                Full colour print
              </li>
              <li className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                Weather-resistant
              </li>
              <li className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                Eyelets included
              </li>
            </ul>
            <Link
              href="/banner-printing/440gsm-pvc-banner"
              className="mt-8 inline-flex items-center justify-center bg-brand-blue px-8 py-3 text-sm font-semibold uppercase tracking-wider text-white transition-colors hover:bg-brand-blue-hover"
            >
              Order 440gsm PVC Banner
            </Link>
          </div>
        </div>
      </section>

      {/* 510gsm Heavy Duty Pricing */}
      <section id="510gsm-heavy-duty-banner" className="scroll-mt-36 py-16 lg:py-24">
        <div className="container">
          <div className="max-w-[56rem] mx-auto">
            <h2 className="text-2xl lg:text-3xl font-bold mb-3">510gsm Premium Heavy Duty Banner</h2>
            <p className="text-[var(--color-text-secondary)] mb-8 leading-relaxed">
              Extra-thick material designed for long-term outdoor exposure and high-wind areas.
            </p>
            <PricingTable widths={pvc510Widths} rows={pvc510Prices} />
            <p className="text-sm font-semibold mt-8 mb-3">Best for:</p>
            <ul className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-[var(--color-text-secondary)]">
              <li className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                Construction companies
              </li>
              <li className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                Long-term promotions
              </li>
              <li className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                Industrial environments
              </li>
            </ul>
            <Link
              href="/banner-printing/510gsm-heavy-duty-banner"
              className="mt-8 inline-flex items-center justify-center bg-brand-blue px-8 py-3 text-sm font-semibold uppercase tracking-wider text-white transition-colors hover:bg-brand-blue-hover"
            >
              Order 510gsm Banner
            </Link>
          </div>
        </div>
      </section>

      {/* Mesh Banner Pricing */}
      <section id="mesh-banner-printing" className="scroll-mt-36 py-16 lg:py-24 bg-[var(--color-off-white)]">
        <div className="container">
          <div className="max-w-[56rem] mx-auto">
            <h2 className="text-2xl lg:text-3xl font-bold mb-3">Mesh Banner Printing for Scaffolding</h2>
            <p className="text-[var(--color-text-secondary)] mb-8 leading-relaxed">
              Wind-resistant mesh banners are ideal for building sites and large outdoor displays.
            </p>
            <PricingTable widths={meshWidths} rows={meshPrices} />
            <p className="text-sm text-[var(--color-text-muted)] mt-4 mb-6">
              Starting from £35 per m².
            </p>
            <p className="text-sm font-semibold mb-3">Perfect for:</p>
            <ul className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-[var(--color-text-secondary)]">
              <li className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                Roofers
              </li>
              <li className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                Builders
              </li>
              <li className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                Scaffolding companies
              </li>
              <li className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                Site advertising
              </li>
            </ul>
            <Link
              href="/banner-printing/mesh-banner-printing"
              className="mt-8 inline-flex items-center justify-center bg-brand-blue px-8 py-3 text-sm font-semibold uppercase tracking-wider text-white transition-colors hover:bg-brand-blue-hover"
            >
              Order Mesh Banner
            </Link>
          </div>
        </div>
      </section>

      {/* 650gsm Event Banner Pricing */}
      <section id="650gsm-heavy-duty-event-banner" className="scroll-mt-36 py-16 lg:py-24">
        <div className="container">
          <div className="max-w-[56rem] mx-auto">
            <h2 className="text-2xl lg:text-3xl font-bold mb-3">650gsm Heavy Duty Event Banners</h2>
            <p className="text-[var(--color-text-secondary)] mb-4 leading-relaxed">
              The ultimate choice for extreme conditions. Our 650gsm banners are engineered for maximum strength, making them the standard for high-speed sporting events and industrial environments.
            </p>
            <ul className="flex flex-col gap-y-2 text-sm text-[var(--color-text-secondary)] mb-8">
              <li><span className="font-semibold text-[var(--color-text-primary)]">Best for:</span> Formula 1 &amp; Motor Racing, Outdoor Festivals, and High-Impact areas.</li>
              <li><span className="font-semibold text-[var(--color-text-primary)]">Durability:</span> Tear-resistant material designed to withstand physical impact and extreme weather.</li>
              <li><span className="font-semibold text-[var(--color-text-primary)]">Finish:</span> High-vibrancy printing with reinforced eyelets included as standard.</li>
            </ul>
            <PricingTable widths={event650Widths} rows={event650Prices} />
            <p className="text-sm text-[var(--color-text-muted)] mt-4 mb-6">
              Custom sizes available — priced per m².
            </p>
            <p className="text-sm font-semibold mb-3">Features:</p>
            <ul className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-[var(--color-text-secondary)]">
              <li className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                Full colour print
              </li>
              <li className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                Weather-resistant
              </li>
              <li className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                Eyelets included
              </li>
            </ul>
            <Link
              href="/banner-printing/650gsm-heavy-duty-event-banner"
              className="mt-8 inline-flex items-center justify-center bg-brand-blue px-8 py-3 text-sm font-semibold uppercase tracking-wider text-white transition-colors hover:bg-brand-blue-hover"
            >
              Order 650gsm Event Banner
            </Link>
          </div>
        </div>
      </section>

      {/* Magnetic Banners Pricing */}
      <section id="magnetic-banners" className="scroll-mt-36 py-16 lg:py-24 bg-[var(--color-off-white)]">
        <div className="container">
          <div className="max-w-[56rem] mx-auto">
            <h2 className="text-2xl lg:text-3xl font-bold mb-3">Magnetic Banners for Vehicles &amp; Shops</h2>
            <p className="text-[var(--color-text-secondary)] mb-4 leading-relaxed">
              Transform your vehicle or storefront in seconds. Our high-grade magnetic banners are perfect for businesses that need flexible branding without permanent decals.
            </p>
            <ul className="flex flex-col gap-y-2 text-sm text-[var(--color-text-secondary)] mb-8">
              <li><span className="font-semibold text-[var(--color-text-primary)]">Vehicle Grade:</span> 0.85mm thick magnetic sheets that stay secure on vans at motorway speeds.</li>
              <li><span className="font-semibold text-[var(--color-text-primary)]">Versatile:</span> Ideal for temporary promotions on shop-fronts or multi-use work vehicles.</li>
              <li><span className="font-semibold text-[var(--color-text-primary)]">Protection:</span> Easy to apply and remove without damaging paintwork or surfaces.</li>
            </ul>
            <PricingTable widths={magneticWidths} rows={magneticPrices} />
            <Link
              href="/banner-printing/magnetic-banners"
              className="mt-8 inline-flex items-center justify-center bg-brand-blue px-8 py-3 text-sm font-semibold uppercase tracking-wider text-white transition-colors hover:bg-brand-blue-hover"
            >
              Order Magnetic Banners
            </Link>
          </div>
        </div>
      </section>

      {/* Custom Stickers & Labels Pricing */}
      <section id="custom-stickers" className="scroll-mt-36 py-16 lg:py-24">
        <div className="container">
          <div className="max-w-[56rem] mx-auto">
            <h2 className="text-2xl lg:text-3xl font-bold mb-3">Custom Product Labels &amp; Stickers</h2>
            <p className="text-[var(--color-text-secondary)] mb-4 leading-relaxed">
              Sharp, vibrant, and water-resistant labels. We print high-quality stickers in Great Barr for everything from brand packaging to equipment marking.
            </p>
            <ul className="flex flex-col gap-y-2 text-sm text-[var(--color-text-secondary)] mb-8">
              <li><span className="font-semibold text-[var(--color-text-primary)]">Quality:</span> Full-colour, high-resolution printing for small text and intricate logos.</li>
              <li><span className="font-semibold text-[var(--color-text-primary)]">Finish:</span> Durable vinyl that is water-resistant and suitable for indoor or outdoor products.</li>
              <li><span className="font-semibold text-[var(--color-text-primary)]">No Minimum Order:</span> Perfect for small businesses or large production runs.</li>
            </ul>
            <div className="overflow-x-auto -mx-4 px-4">
              <table className="w-full text-sm border-collapse min-w-[600px]">
                <thead>
                  <tr>
                    <th className="text-left p-3 bg-black text-white font-semibold">Qty</th>
                    {stickerSizes.map((s) => (
                      <th key={s} className="p-3 bg-black text-white font-semibold text-center">{s}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {stickerPrices.map((row, i) => (
                    <tr key={row.qty} className={i % 2 === 0 ? 'bg-white' : 'bg-[var(--color-off-white)]'}>
                      <td className="p-3 font-semibold border-b border-[var(--color-border)]">{row.qty}</td>
                      {row.prices.map((price, j) => (
                        <td key={j} className="p-3 text-center border-b border-[var(--color-border)]">
                          <span className="font-semibold">{price}</span>
                          <span className="block text-xs text-[var(--color-text-muted)]">{row.perUnit[j]}</span>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Link
              href="/banner-printing/custom-stickers"
              className="mt-8 inline-flex items-center justify-center bg-brand-blue px-8 py-3 text-sm font-semibold uppercase tracking-wider text-white transition-colors hover:bg-brand-blue-hover"
            >
              Order Stickers &amp; Labels
            </Link>
          </div>
        </div>
      </section>

      {/* Production & Quality Section */}
      <section className="py-16 lg:py-24">
        <div className="container">
          <div className="max-w-[48rem] mx-auto">
            <h2 className="text-2xl lg:text-3xl font-bold mb-6">
              Printed In-House Using Professional Equipment
            </h2>
            <p className="text-[var(--color-text-secondary)] leading-relaxed mb-8">
              We use large-format printing technology to produce sharp, vibrant banners with consistent colour accuracy and fast turnaround. Printing in-house means:
            </p>
            <div className="grid sm:grid-cols-3 gap-6">
              <div className="text-center p-8 border border-[var(--color-border)]">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-[var(--color-off-white)]">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">Faster Delivery</h3>
                <p className="text-sm text-[var(--color-text-secondary)]">
                  No outsourcing delays — most orders ready in 1–3 days.
                </p>
              </div>
              <div className="text-center p-8 border border-[var(--color-border)]">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-[var(--color-off-white)]">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">Better Quality Control</h3>
                <p className="text-sm text-[var(--color-text-secondary)]">
                  Every banner inspected before it leaves our shop.
                </p>
              </div>
              <div className="text-center p-8 border border-[var(--color-border)]">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-[var(--color-off-white)]">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">Competitive Pricing</h3>
                <p className="text-sm text-[var(--color-text-secondary)]">
                  No middleman markup — direct from our print shop to you.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* YouTube Video Section */}
      <section className="py-16 lg:py-24 bg-[var(--color-off-white)]">
        <div className="container">
          <div className="max-w-[48rem] mx-auto">
            <h2 className="text-2xl lg:text-3xl font-bold text-center mb-8">
              See Our Banner Printing Process
            </h2>
            <div className="relative w-full aspect-[9/16] sm:aspect-video max-w-[24rem] sm:max-w-none mx-auto">
              {/* SEO: added loading="lazy" — remove attribute to revert */}
              <iframe
                src="https://www.youtube.com/embed/uuUPacT8BsE"
                title="Banner Printing Process at OK T-Shirt Birmingham"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
                className="absolute inset-0 w-full h-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 lg:py-24">
        <div className="container">
          <div className="max-w-[48rem] mx-auto">
            <h2 className="text-2xl lg:text-3xl font-bold text-center mb-12">
              Frequently Asked Questions
            </h2>
            <div>
              {faqs.map((faq) => (
                <FAQItem key={faq.question} question={faq.question} answer={faq.answer} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Order CTA Section */}
      <section className="py-20 lg:py-32 bg-black">
        <div className="container">
          <div className="max-w-[64rem] mx-auto text-center">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 tracking-tight leading-tight text-white">
              Need Banner Printing
              <br className="hidden sm:block" />
              <span className="sm:hidden"> </span>
              in Birmingham?
            </h2>
            <p className="text-lg lg:text-xl text-gray-300 mb-4 leading-relaxed">
              Contact OKTshirt today for a fast quote.
            </p>
            <div className="flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm text-gray-400 mb-10">
              <span className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" /></svg>
                Great Barr, Birmingham
              </span>
              <span className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" /></svg>
                020 4520 8521
              </span>
              <span className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" /></svg>
                WhatsApp available
              </span>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                href="/contact"
                className="group inline-flex items-center justify-center gap-2 px-10 py-4 text-base font-semibold bg-white text-black hover:bg-gray-100 transition-all duration-300"
              >
                Get a Quote
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </Link>
              <a
                href="tel:02045208521"
                className="inline-flex items-center justify-center px-10 py-4 text-base font-semibold border border-white text-white hover:bg-white hover:text-black transition-all duration-300"
              >
                Call Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Upsell Cross-Links */}
      <section className="py-16 lg:py-24 bg-[var(--color-off-white)]">
        <div className="container">
          <div className="max-w-[48rem] mx-auto">
            <h2 className="text-2xl lg:text-3xl font-bold text-center mb-8">
              More From OK T-Shirt
            </h2>
            <div className="grid sm:grid-cols-3 gap-6">
              <Link
                href="/collections/workwear"
                className="block p-8 bg-white border border-[var(--color-border)] text-center hover:border-black transition-colors"
              >
                <h3 className="text-lg font-semibold mb-2">Branded Workwear</h3>
                <p className="text-sm text-[var(--color-text-secondary)]">
                  Custom printed and embroidered workwear for your team.
                </p>
              </Link>
              <Link
                href="/collections/school-uniform"
                className="block p-8 bg-white border border-[var(--color-border)] text-center hover:border-black transition-colors"
              >
                <h3 className="text-lg font-semibold mb-2">Embroidered Uniforms</h3>
                <p className="text-sm text-[var(--color-text-secondary)]">
                  Professional embroidery for schools and businesses.
                </p>
              </Link>
              <Link
                href="/services"
                className="block p-8 bg-white border border-[var(--color-border)] text-center hover:border-black transition-colors"
              >
                <h3 className="text-lg font-semibold mb-2">All Our Services</h3>
                <p className="text-sm text-[var(--color-text-secondary)]">
                  Screen printing, embroidery, DTG and more.
                </p>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
